import { useEffect, useState } from "react";
import { useParams, useLocation, Navigate, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PERSONAS } from "@/lib/personas";
import { setLandingPersona, getAttribution } from "@/lib/utm";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/site";

export default function PersonaPage() {
  const params = useParams();
  const { pathname } = useLocation();
  const slug = params.slug || pathname.replace(/^\//, "").split("/")[0];
  const persona = PERSONAS[slug];
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (persona) setLandingPersona(persona.slug);
  }, [persona]);

  if (!persona) return <Navigate to="/" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill all fields.");
      return;
    }
    setSubmitting(true);
    try {
      const attr = getAttribution();
      await api.post("/inquiries", {
        ...form,
        loan_type: persona.loanTypeDefault,
        employment_type: persona.employmentDefault,
        city: "Bangalore",
        message: `Inquiry via ${persona.eyebrow} landing page`,
        persona: persona.slug,
        landing_page: window.location.pathname,
        utm_source: attr.utm_source,
        utm_medium: attr.utm_medium,
        utm_campaign: attr.utm_campaign,
        utm_content: attr.utm_content,
        utm_term: attr.utm_term,
        referrer: attr.referrer,
      });
      setDone(true);
      toast.success("Got it — Raj will reach out shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={`persona-page-${persona.slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-forest text-brand-cream">
        <div className="absolute inset-0 z-0">
          <img src={persona.image} alt="" className="h-full w-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-forest/95 via-brand-forest/85 to-brand-forest/55" />
          <div className="absolute inset-0 bg-grain opacity-25" />
        </div>
        <div className="container-x relative z-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <span className="eyebrow text-brand-gold">{persona.eyebrow}</span>
            <h1 className="mt-4 font-outfit font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {persona.headline}
            </h1>
            <p className="mt-6 text-lg text-brand-cream/80 leading-relaxed max-w-xl">
              {persona.subhead}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#mini-form"
                data-testid={`persona-${persona.slug}-cta-form`}
                className="btn-primary text-base"
              >
                {persona.formCTA} <ArrowRight className="h-4 w-4" />
              </a>
              <a
                data-testid={`persona-${persona.slug}-cta-whatsapp`}
                href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(`Hi Raj, I'm a ${persona.eyebrow.replace("For ", "")} — would like to discuss.`)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-gold/40 bg-brand-cream/5 px-6 py-3 text-sm font-semibold text-brand-cream backdrop-blur-md transition-all hover:bg-brand-cream/10"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp Raj
              </a>
            </div>
          </div>

          {/* Mini form */}
          <div id="mini-form" className="md:col-span-5">
            <div className="card-soft p-6 md:p-7 bg-white text-brand-forest">
              {done ? (
                <div data-testid={`persona-${persona.slug}-success`} className="text-center py-6">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-brand-terracotta/10 text-brand-terracotta">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-outfit font-bold text-xl">Got it. Raj will call you.</h3>
                  <p className="mt-3 text-sm text-brand-sage">
                    Expect a call from {BRAND.phone} within one business day.
                  </p>
                  <div className="mt-5 flex justify-center gap-2">
                    <a href={`tel:${BRAND.phoneE164}`} className="btn-primary text-sm">
                      <Phone className="h-4 w-4" /> Call Now
                    </a>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid={`persona-${persona.slug}-form`}>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-brand-terracotta font-semibold">Free Consultation</div>
                    <h3 className="mt-1 font-outfit font-bold text-2xl">2-min call. No spam.</h3>
                  </div>
                  <div>
                    <Label htmlFor="p-name" className="text-sm font-medium">Full Name</Label>
                    <Input id="p-name" data-testid={`persona-${persona.slug}-name`} value={form.name} onChange={set("name")} placeholder="Your name" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="p-phone" className="text-sm font-medium">Phone</Label>
                    <Input id="p-phone" data-testid={`persona-${persona.slug}-phone`} value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="p-email" className="text-sm font-medium">Email</Label>
                    <Input id="p-email" data-testid={`persona-${persona.slug}-email`} type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-1.5" />
                  </div>
                  <button type="submit" disabled={submitting} data-testid={`persona-${persona.slug}-submit`} className="btn-primary w-full text-base disabled:opacity-60">
                    {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>) : (<>{persona.formCTA} <ArrowRight className="h-4 w-4" /></>)}
                  </button>
                  <p className="text-[11px] text-brand-sage text-center">
                    Your details are used only to reach back about this inquiry.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="container-x py-20 md:py-24">
        <div className="max-w-2xl">
          <span className="eyebrow">Why borrowers like you choose us</span>
          <h2 className="mt-4 h-display text-3xl sm:text-4xl">
            Built for the way {persona.eyebrow.replace("For ", "").toLowerCase()} actually borrow.
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {persona.benefits.map((b, i) => (
            <div key={b.t} data-testid={`persona-${persona.slug}-benefit-${i}`} className="card-soft p-6">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-forest text-brand-gold">
                  <CheckCircle2 className="h-5 w-5" />
                </span>
                <h3 className="font-outfit font-bold text-lg text-brand-forest">{b.t}</h3>
              </div>
              <p className="mt-3 text-brand-sage leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="bg-brand-sand/50 py-20 md:py-24">
        <div className="container-x">
          <span className="eyebrow">What we'll line up for you</span>
          <h2 className="mt-4 h-display text-3xl sm:text-4xl">Products tailored to your profile.</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {persona.products.map((p, i) => (
              <div key={p.title} data-testid={`persona-${persona.slug}-product-${i}`} className="rounded-2xl border border-brand-sand bg-white p-6">
                <h3 className="font-outfit font-bold text-xl text-brand-forest">{p.title}</h3>
                <p className="mt-2 text-sm text-brand-sage leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-x py-20 md:py-24">
        <div className="max-w-2xl">
          <span className="eyebrow">Frequently asked</span>
          <h2 className="mt-4 h-display text-3xl sm:text-4xl">Answers, before you ask.</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 max-w-3xl">
          {persona.faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`persona-${persona.slug}-faq-${i}`} className="border-brand-sand">
              <AccordionTrigger className="text-left font-outfit font-semibold text-brand-forest text-base hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-brand-sage leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* BOTTOM CTA */}
      <section className="container-x pb-20 md:pb-28">
        <div className="rounded-3xl bg-brand-forest p-10 md:p-14 text-brand-cream relative overflow-hidden">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-brand-terracotta/20 blur-2xl" />
          <div className="absolute -left-10 bottom-0 h-48 w-48 rounded-full bg-brand-gold/20 blur-2xl" />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl tracking-tight">
                Ready to move from confusion to clarity?
              </h2>
              <p className="mt-3 text-brand-cream/75">
                One free call. We'll tell you exactly what's possible — and what's not — for your case.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end gap-3">
              <Link to="/apply" data-testid={`persona-${persona.slug}-bottom-apply`} className="btn-primary">
                Full application <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
